import { Box, ButtonBase, Collapse, Tooltip, SvgIcon, Popper, Paper } from "@mui/material";
import { LockOutlined, WarningAmberOutlined } from "@mui/icons-material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import React, { ReactNode, useCallback } from "react";
import history from "utils/history";
import { setNavReturnFocus } from "hooks/use-nav-return-focus";
import { useFeatureState } from "hooks/use-feature";
import strings from "global/constants/StringConstants";
import { nav } from "theme/colors";
import NavBetaBadge from "./NavBetaBadge";

interface SideNavItemProps {
  active?: boolean;
  highlighted?: boolean;
  children?: ReactNode;
  depth?: number;
  disabled?: boolean;
  icon?: ReactNode;
  activeIcon?: ReactNode;
  title: string;
  path?: string;
  isOpen?: boolean;
  isCollapsed: boolean;
  onToggle: (title: string) => void;
  onItemClick: (title: string, hasChildren: boolean, depth: number) => void;
  featureKey?: string;
  beta?: boolean;
}

const SideNavItem = ({
  active,
  highlighted,
  children,
  depth = 0,
  disabled,
  icon,
  activeIcon,
  title,
  path,
  isOpen,
  isCollapsed: isCollapsedProp,
  onToggle,
  onItemClick,
  featureKey,
  beta,
}: SideNavItemProps) => {
  const isCollapsed = depth > 0 ? false : isCollapsedProp;
  const hasChildren = !!children;
  const isHighlighted = highlighted !== undefined ? highlighted : active;
  // Sub-level rows read differently from top-level ones: no icon, and the
  // active row is a solid primary pill against the rail rather than a tint.
  const isSubItem = depth > 0;

  const featureState = useFeatureState(featureKey ?? "");
  const isGated =
    !!featureKey && featureState.status !== strings.FEATURE_STATUS_AVAILABLE;
  const gateTooltip = isGated
    ? featureState.status === strings.FEATURE_STATUS_NOT_IN_PLAN
      ? "Upgrade your plan to access this feature"
      : ((featureState as any).disabledMessage ??
        "This feature is temporarily unavailable")
    : "";
  // The collapsed rail hides labels, so the badge has to ride in the tooltip.
  const railTitle = beta ? `${title} (Beta)` : title;

  // Rows that navigate render as a real <a href> so the browser's own
  // affordances work: context menu ("Open link in new tab"), middle-click,
  // modifier-clicks, hover status bar, copy link address. Group rows only
  // expand, so they stay buttons.
  const isLink = !!path && !hasChildren;

  const handleClick = useCallback(
    (event: React.MouseEvent) => {
      // Modifier-clicks on a link belong to the browser — opening a new tab or
      // window is its job, not ours.
      if (
        isLink &&
        (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey)
      ) {
        return;
      }
      if (event.button !== 0) return;
      // A plain click stays client-side; without this the anchor would trigger
      // a full page load.
      if (isLink) event.preventDefault();
      onToggle(title);
      onItemClick(title, hasChildren, depth);
      if (isLink && path) {
        if (event.detail === 0) {
          setNavReturnFocus(triggerRef.current, path);
        }
        history.push(path);
      }
    },
    [title, onToggle, onItemClick, hasChildren, path, depth, isLink],
  );

  const handleContextMenu = useCallback(
    (event: React.MouseEvent) => {
      // Non-navigating rows have nothing to offer a context menu.
      if (!isLink) {
        event.preventDefault();
      }
    },
    [isLink],
  );

  const [openPopover, setOpenPopover] = React.useState(false);
  const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null);
  const timeoutRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const triggerRef = React.useRef<HTMLElement | null>(null);
  const flyoutRef = React.useRef<HTMLDivElement | null>(null);
  const focusFlyoutRef = React.useRef(false);
  const flyoutId = `side-nav-flyout-${title
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")}`;

  const clearCloseTimer = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  };

  const closeFlyout = React.useCallback((returnFocus = false) => {
    clearCloseTimer();
    focusFlyoutRef.current = false;
    setOpenPopover(false);
    setAnchorEl(null);
    if (returnFocus) triggerRef.current?.focus();
  }, []);

  const handleMouseEnter = (event: React.MouseEvent<HTMLElement>) => {
    if (!isCollapsedProp || !hasChildren) return;
    clearCloseTimer();
    setAnchorEl(event.currentTarget);
    setOpenPopover(true);
  };

  const handleMouseLeave = () => {
    if (!isCollapsedProp) return;
    timeoutRef.current = setTimeout(() => {
      setOpenPopover(false);
      setAnchorEl(null);
    }, 150);
  };

  const handlePopoverMouseEnter = () => {
    clearCloseTimer();
  };

  const handlePopoverMouseLeave = () => {
    timeoutRef.current = setTimeout(() => {
      setOpenPopover(false);
      setAnchorEl(null);
    }, 150);
  };

  const handleTriggerKeyDown = (event: React.KeyboardEvent<HTMLElement>) => {
    if (!isCollapsedProp || !hasChildren) return;
    if (event.key === "Escape") {
      if (openPopover) closeFlyout(true);
      return;
    }
    if (
      event.key === "Enter" ||
      event.key === " " ||
      event.key === "ArrowRight" ||
      event.key === "ArrowDown"
    ) {
      event.preventDefault();
      clearCloseTimer();
      focusFlyoutRef.current = true;
      setAnchorEl(triggerRef.current);
      setOpenPopover(true);
    }
  };

  React.useEffect(() => {
    if (!openPopover || !focusFlyoutRef.current) return;
    const first = flyoutRef.current?.querySelector<HTMLElement>(
      'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])',
    );
    first?.focus();
    focusFlyoutRef.current = false;
  }, [openPopover]);

  React.useEffect(() => clearCloseTimer, []);

  const offset = depth === 0 ? 0 : (depth - 1) * 16;

  return (
      <li
        style={{
          width: "100%",
          listStyle: "none",
          display: isCollapsed ? "flex" : "block",
          justifyContent: isCollapsed ? "center" : undefined,
        }}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <ButtonBase
          ref={triggerRef as any}
          onClick={handleClick}
          onContextMenu={handleContextMenu}
          onKeyDown={handleTriggerKeyDown}
          disabled={disabled}
          component={isLink ? "a" : "button"}
          href={isLink ? path : undefined}
          aria-label={isCollapsed ? railTitle : undefined}
          aria-current={active && isLink ? "page" : undefined}
          aria-haspopup={hasChildren && isCollapsedProp ? "menu" : undefined}
          aria-controls={
            hasChildren && isCollapsedProp && openPopover ? flyoutId : undefined
          }
          aria-expanded={
            hasChildren
              ? isCollapsedProp
                ? openPopover
                : !!isOpen
              : undefined
          }
          sx={{
            alignItems: "center",
            boxSizing: "border-box",
            borderRadius: isSubItem ? "10px" : "11px",
            display: "flex",
            justifyContent: isCollapsed ? "center" : "flex-start",
            pl: `${14 + offset}px`,
            pr: `${14 + offset}px`,
            py: isSubItem ? "9px" : "11px",
            width: "100%",
            color: "",
            border: "none",
            boxShadow: "none",
            background: active ? "var(--nav-item-active-bg)" : "transparent",
            textDecoration: "none",
            position: "relative",
            "&:hover": {
              background: active
                ? "var(--nav-item-active-bg)"
                : "var(--nav-item-hover-bg)",
            },
            "&.Mui-focusVisible": {
              outline: (theme) => `2px solid ${theme.palette.primary.main}`,
              outlineOffset: "-2px",
            },
            // Both depths wear the same active treatment, so this marker on the
            // rail the child group hangs off is what separates a selected
            // sub-row from a selected top-level one.
            ...(isSubItem &&
              active && {
                "&::before": {
                  content: '""',
                  position: "absolute",
                  left: "-13px",
                  top: "50%",
                  transform: "translateY(-50%)",
                  width: "3px",
                  height: "20px",
                  borderRadius: "2px",
                  background: "var(--nav-item-active-bar)",
                },
              }),
          }}
        >
          {(icon || activeIcon) &&
            !isSubItem &&
            (isCollapsed ? (
              <Tooltip
                title={isGated ? `${railTitle} — ${gateTooltip}` : railTitle}
                placement="right"
                arrow
              >
                <Box
                  component="span"
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: isGated
                      ? "var(--nav-item-disabled-color, #C3C3C3)"
                      : isHighlighted
                        ? "var(--nav-item-icon-active-color)"
                        : "var(--nav-item-icon-color)",
                    backgroundColor: active
                      ? "var(--nav-item-active-bg)"
                      : "transparent",
                    borderRadius: "50%",
                    boxShadow: "none",
                    // Same 20px clamp as the expanded branch — without it the
                    // icons fall back to SvgIcon's 24px default and the rail
                    // reads bigger than the open nav.
                    "& svg": { fontSize: 20 },
                    transition:
                      "background 0.15s, color 0.15s, box-shadow 0.15s",
                    "&:hover": {
                      backgroundColor: !active
                        ? "var(--nav-item-hover-bg)"
                        : undefined,
                    },
                    ...(isGated && {
                      // Stroke only — see the note on the expanded branch.
                      "& svg, & svg *": {
                        stroke: "var(--nav-item-disabled-color, #C3C3C3)",
                      },
                    }),
                  }}
                >
                  {active && activeIcon ? activeIcon : icon}
                </Box>
              </Tooltip>
            ) : (
              <Box
                component="span"
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: isGated
                    ? "var(--nav-item-disabled-color, #C3C3C3)"
                    : isHighlighted
                      ? "var(--nav-item-icon-active-color)"
                      : "var(--nav-item-icon-color)",
                  mr: "12px",
                  "& svg": { fontSize: 20 },
                  ...(isGated && {
                    // Stroke only — never `fill`. The nav set is stroke
                    // drawings carrying a `fill="none"` attribute, and a CSS
                    // fill here would beat that attribute and render every
                    // glyph as a solid blob.
                    "& svg, & svg *": {
                      stroke: "var(--nav-item-disabled-color, #C3C3C3)",
                    },
                  }),
                }}
              >
                {active && activeIcon ? activeIcon : icon}
              </Box>
            ))}
          {!isCollapsed && (
            <Tooltip
              title={gateTooltip}
              disableHoverListener={!isGated}
              placement="right"
              arrow
            >
              <Box
                component="span"
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                  color: isGated
                    ? "var(--nav-item-disabled-color, #C3C3C3)"
                    : (isSubItem ? active : isHighlighted)
                      ? "var(--nav-item-active-color)"
                      : "var(--nav-item-color)",
                  fontWeight: (isSubItem ? active : isHighlighted) ? 700 : 600,
                  fontFamily: (theme) => theme.typography.fontFamily,
                  fontSize: 14,
                  lineHeight: "20px",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {title}
                {beta && <NavBetaBadge />}
                {isGated &&
                  (featureState.status === strings.FEATURE_STATUS_ADMIN_DISABLED ? (
                    <WarningAmberOutlined
                      sx={{ fontSize: 13, opacity: 0.6, flexShrink: 0 }}
                    />
                  ) : (
                    <LockOutlined
                      sx={{ fontSize: 13, opacity: 0.6, flexShrink: 0 }}
                    />
                  ))}
              </Box>
            </Tooltip>
          )}
          {!isCollapsed && hasChildren && (
            <SvgIcon
              sx={{
                color: "var(--nav-item-chevron-color)",
                fontSize: 15,
                ml: "auto",
              }}
            >
              {isOpen ? <ExpandMoreIcon /> : <ChevronRightIcon />}
            </SvgIcon>
          )}
          {isCollapsedProp && hasChildren && (
            <SvgIcon
              sx={{
                color: "var(--nav-item-chevron-color)",
                fontSize: 12,
                position: "absolute",
                right: 8,
              }}
            >
              <ChevronRightIcon />
            </SvgIcon>
          )}
        </ButtonBase>
        {hasChildren && !isCollapsedProp && (
          <Collapse in={isOpen} unmountOnExit>
            {children}
          </Collapse>
        )}
        {hasChildren && isCollapsedProp && (
          <Popper
            open={openPopover}
            anchorEl={anchorEl}
            placement="right-start"
            modifiers={[
              {
                name: "offset",
                options: {
                  offset: [0, 16],
                },
              },
            ]}
            sx={{ zIndex: 1200, pl: "6px" }}
            onMouseEnter={handlePopoverMouseEnter}
            onMouseLeave={handlePopoverMouseLeave}
          >
            <Paper
              id={flyoutId}
              ref={flyoutRef}
              onKeyDown={(event) => {
                if (event.key === "Escape" || event.key === "Tab") {
                  event.preventDefault();
                  event.stopPropagation();
                  closeFlyout(true);
                }
              }}
              onBlur={(event) => {
                if (
                  !event.currentTarget.contains(
                    event.relatedTarget as Node | null,
                  ) &&
                  event.relatedTarget !== triggerRef.current
                ) {
                  closeFlyout();
                }
              }}
              onClick={() => {
                setOpenPopover(false);
                setAnchorEl(null);
              }}
              sx={(theme) => ({
                "--nav-item-icon-color": nav.itemText,
                "--nav-item-icon-active-color": nav.activeIcon,
                "--nav-item-chevron-color": nav.itemText,
                "--nav-item-active-color": nav.activeText,
                "--nav-item-color": nav.itemText,
                "--nav-item-hover-bg": nav.hoverBg,
                "--nav-item-active-bg": nav.activeBg,
                // The flyout renders in a portal, outside the drawer that
                // declares this — so it has to restate it.
                "--nav-item-active-bar": nav.activeBar,
                background: `var(--nav-bg, ${theme.palette.common.white})`,
                border: `1px solid var(--nav-border-color, ${theme.palette.neutral[200]})`,
                boxShadow: "0px 8px 24px rgba(0, 0, 0, 0.08)",
                borderRadius: "8px",
                overflow: "hidden",
                minWidth: "200px",
              })}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  py: "12px",
                  px: "20px",
                  borderBottom: "1px solid var(--nav-border-color)",
                  color: "var(--nav-item-active-color)",
                  background: "var(--nav-item-hover-bg)",
                  fontWeight: 700,
                  fontSize: 14,
                }}
              >
                {icon && (
                  <Box sx={{ display: "inline-flex", mr: 1.5, color: "var(--nav-item-icon-active-color)" }}>
                    {activeIcon ? activeIcon : icon}
                  </Box>
                )}
                <Box sx={{ flexGrow: 1 }}>{title}</Box>
                <SvgIcon sx={{ fontSize: 14, color: "var(--nav-item-chevron-color)" }}>
                  <ExpandMoreIcon />
                </SvgIcon>
              </Box>
              <Box sx={{ p: "8px" }}>
                {children}
              </Box>
            </Paper>
          </Popper>
        )}
      </li>
  );
};

export default SideNavItem;
